import React from 'react';
import {
  formatOmdPrice,
  formatOmdUvpFromListPrice,
  labelTaxCode,
  type OmdProduct,
} from '../../../utils/omdProductView';
import type { OmdPanelMaterial } from '../types';
import type { OmdPanelSettings } from '../../../utils/omdPanelSettings';
import { OmdRawJsonSection } from '../shared/OmdRawJsonSection';
import { OmdSection } from '../shared/OmdSection';
import { OmdVkCalculator } from '../shared/OmdVkCalculator';

type OmdPreiseTabProps = {
  product: OmdProduct;
  rawProduct: unknown;
  material: OmdPanelMaterial;
  settings: OmdPanelSettings;
  hideRawJson?: boolean;
};

function PriceCard({ label, value, highlight }: { label: string; value: string | null; highlight?: boolean }) {
  if (!value && !highlight) return null;
  return (
    <div className="rounded-lg border border-app-border bg-app-surface px-3 py-2.5">
      <p className="text-xs font-medium text-app-text-muted">{label}</p>
      <p
        className={`mt-0.5 text-lg font-semibold tabular-nums ${
          highlight ? 'text-pipedrive-blue' : 'text-app-text'
        }`}
      >
        {value ?? '-'}
      </p>
    </div>
  );
}

export function OmdPreiseTab({ product, rawProduct, material, settings, hideRawJson }: OmdPreiseTabProps) {
  const netPrice = formatOmdPrice(product.prices?.netPrice);
  const listPrice = formatOmdPrice(product.prices?.listPrice);
  const uvp = formatOmdUvpFromListPrice(product);
  const taxLabel = labelTaxCode(product.prices?.taxCode);
  const rawMaterials = product.prices?.rawMaterial ?? [];

  if (product.basic?.priceOnDemand) {
    return (
      <p className="text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
        Preis nur auf Anfrage
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <PriceCard label="Netto-EK" value={netPrice} highlight />
        <PriceCard label="Netto Listenpreis" value={listPrice} />
        <PriceCard label="Brutto Listenpreis" value={uvp} />
      </div>

      {(taxLabel || product.prices?.billBasis) && (
        <div className="text-xs text-app-text-subtle space-y-0.5">
          {taxLabel && <p>MwSt.: {taxLabel}</p>}
          {product.prices?.billBasis && <p>Preisbasis: {product.prices.billBasis}</p>}
        </div>
      )}

      <OmdVkCalculator product={product} material={material} />

      {rawMaterials.length > 0 && (
        <OmdSection title="Materialzuschläge" settings={settings}>
          <div className="overflow-x-auto rounded-lg border border-app-border">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-app-surface-muted border-b border-app-border">
                  <th className="px-3 py-2 text-left text-xs font-semibold text-app-text-muted">Beschreibung</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-app-text-muted">Wert</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-app-border">
                {rawMaterials.map((row, idx) => (
                  <tr key={idx}>
                    <td className="px-3 py-2 text-app-text">{row.description || '-'}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-app-text">
                      {row.value ? `${row.value}${row.unit ? ` ${row.unit}` : ''}` : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </OmdSection>
      )}

      {!hideRawJson && <OmdRawJsonSection product={rawProduct} />}
    </div>
  );
}
